import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Button } from '@shared/components';
import { getUser } from '../../state/authSlice';
import { getIsReviewsEnabled } from '../../state/courierSlice';
import { useDeliveredPackages } from '../../hooks/useDeliveredPackages';
import { useCustomerReviews } from '../../hooks/useCustomerReviews';
import { pickPackageToReview, readSnooze, shouldPromptReview, writeSnooze } from '../../utils/reviewPrompt';
import StarRatingInput from './StarRatingInput';

interface PromptBodyProps {
  userId: number | string;
  snoozedUntil: Date | null;
}

/**
 * The half that talks to the server. Only mounted for users who could act on
 * the prompt, so the two requests do not run on every page load.
 */
const PromptBody = ({ userId, snoozedUntil }: PromptBodyProps) => {
  const navigate = useNavigate();
  const { packages, isLoaded: packagesLoaded } = useDeliveredPackages();
  const { reviews, isLoading: reviewsLoading } = useCustomerReviews();
  const [dismissed, setDismissed] = useState(false);
  const [rating, setRating] = useState(0);

  if (dismissed || !packagesLoaded || reviewsLoading) return null;

  const input = {
    isReviewsEnabled: true,
    deliveredPackages: packages.map((p) => ({ id: p.id as number, trackingNo: p.trackingNo, description: p.description, deliveredDate: p.deliveredDate as any })),
    reviewedPackageIds: reviews.map((r) => r.packageId).filter((id): id is number => typeof id === 'number'),
    snoozedUntil,
    now: new Date(),
  };
  if (!shouldPromptReview(input)) return null;
  const pkg = pickPackageToReview(input);
  if (!pkg) return null;

  const handleNotNow = () => {
    writeSnooze(userId);
    setDismissed(true);
  };

  const handleWrite = () => {
    setDismissed(true);
    const params = new URLSearchParams({ packageId: String(pkg.id) });
    if (rating) params.set('rating', String(rating));
    navigate(`/reviews?${params.toString()}`);
  };

  return (
    <Modal isOpen onClose={handleNotNow} title="How was your delivery?" size="small">
      <div className="space-y-4" data-testid="review-prompt">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Your package <span className="font-semibold text-gray-900 dark:text-white">#{pkg.trackingNo}</span> was delivered recently.
          Tap a star to tell us how it went — your review helps other customers.
        </p>
        <div className="flex justify-center">
          <StarRatingInput value={rating} onChange={setRating} label="How would you rate this delivery?" />
        </div>
        <Button variant="primary" fullWidth onClick={handleWrite}>
          Write a review
        </Button>
        <button
          type="button"
          onClick={handleNotNow}
          className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Not now
        </button>
      </div>
    </Modal>
  );
};

/**
 * Asks a signed-in customer to review a recent delivery. The outer component
 * answers everything it can locally (feature on? live snooze?) before mounting
 * the half that hits the API.
 */
const ReviewPrompt = () => {
  const user = useSelector(getUser);
  const isReviewsEnabled = useSelector(getIsReviewsEnabled);
  const location = useLocation();

  if (!user?.id || user?.isCourier) return null;
  if (!isReviewsEnabled) return null;
  // Already on the review form — asking again there is just noise.
  if (location.pathname.startsWith('/reviews')) return null;

  const snoozedUntil = readSnooze(user.id);
  if (snoozedUntil && snoozedUntil > new Date()) return null;

  return <PromptBody userId={user.id} snoozedUntil={snoozedUntil} />;
};

export default ReviewPrompt;
